import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { Button } from "react-bootstrap"
import { useLocation, useNavigate } from "react-router-dom"
import api from "../api/api"
import authService from "../services/authService"
import { lockWalkthroughInteractions } from "../utils/walkthroughInteraction"
import {
  WALKTHROUGH_FORM_PROGRESS_EVENT,
  WALKTHROUGH_HISTORY_STATE_KEY,
  WALKTHROUGH_PREVIEW_VIEWED_EVENT,
  WALKTHROUGH_REFRESH_EVENT,
  WALKTHROUGH_REVIEW_CUE_EVENT,
} from "../utils/walkthroughEvents"
import "../css/TherapistWalkthrough.css"

const STEP_COUNT = 9

const stepCopy = {
  practice_client: {
    number: 1,
    title: "Meet your Test Client",
    body: "Start here so you can learn the Plasticise workflow confidently before working with real clients.",
    selector: '[data-walkthrough="practice-client"]',
  },
  add_program: {
    number: 2,
    title: "Add a test program",
    body: "Create a simple program for your Test Client. Nothing here is sent to a real client.",
    selector: '[data-walkthrough="add-program"]',
  },
  program_name: {
    number: 3,
    title: "Name your program",
    body: "Give this test program a clear name.",
    selector: '[data-walkthrough="program-name"]',
  },
  exercise_name: {
    number: 3,
    title: "Name Activity 1",
    body: "Give Activity 1 a clear name, then move to Instructions.",
    selector: '[data-walkthrough="exercise-name"]',
  },
  exercise_instructions: {
    number: 3,
    title: "Add instructions for Activity 1",
    body: "Add a short instruction, then finish editing to continue (optional).",
    selector: '[data-walkthrough="exercise-instructions"]',
  },
  media: {
    number: 4,
    title: "Add media",
    body: "Capture the exact activity, guidance or exercise for your test client (optional).",
    selector: '[data-walkthrough="media"]',
  },
  end_date: {
    number: 5,
    title: "Set the program end date",
    body: "Choose when this test program should finish. You can change it later if needed.",
    selector: '[data-walkthrough="end-date"]',
  },
  schedule: {
    number: 6,
    title: "Set the schedule",
    body: "Choose the days you want the client to complete the program.",
    selector: '[data-walkthrough="schedule"]',
  },
  publish: {
    number: 7,
    title: "Send to client via email",
    body: "Straight to your clients device before the end of the session via an email link",
    selector: '[data-walkthrough="publish"]',
  },
  preview: {
    number: 8,
    title: "Preview what the client sees",
    body: "Look at your clients personalised program ready to go so they can practice between sessions",
    selector: '[data-walkthrough="preview"]',
  },
  complete: {
    number: 9,
    title: "You're ready to Plasticise!",
    body: "You've completed the Test workflow and are ready to add your first real client",
    selector: '[data-walkthrough="finish-onboarding"]',
  },
}

const getWalkthroughTipKey = (step) =>
  step ? `${step.id}:${step.selector || "fallback"}` : null

const createWalkthroughHistoryCue = (step) => ({
  key: getWalkthroughTipKey(step),
  step: { ...step },
})

const cueSequence = [
  { id: "practice_client", ...stepCopy.practice_client },
  { id: "add_program", ...stepCopy.add_program },
  { id: "exercise", ...stepCopy.program_name },
  { id: "exercise", ...stepCopy.exercise_name },
  { id: "exercise", ...stepCopy.exercise_instructions },
  { id: "media", ...stepCopy.media },
  { id: "end_date", ...stepCopy.end_date },
  { id: "schedule", ...stepCopy.schedule },
  { id: "publish", ...stepCopy.publish },
  { id: "preview", ...stepCopy.preview },
  { id: "complete", ...stepCopy.complete },
]

const getCueSequenceIndex = (step) => {
  const key = getWalkthroughTipKey(step)
  return cueSequence.findIndex((cue) => getWalkthroughTipKey(cue) === key)
}

const getProgramFormStep = (state, formProgress) => {
  const programNamed =
    formProgress?.programNamed ?? state?.program_named ?? false
  const exerciseAdded =
    formProgress?.exerciseAdded ?? state?.exercise_added ?? false
  const mediaAdded =
    formProgress?.mediaAdded ?? state?.media_added ?? false
  const endDateSet =
    formProgress?.endDateSet ?? state?.schedule_set ?? false
  const scheduleSet =
    formProgress?.scheduleSet ?? state?.schedule_set ?? false

  if (!programNamed) {
    return {
      id: "exercise",
      ...stepCopy.program_name,
    }
  }

  if (!exerciseAdded) {
    return {
      id: "exercise",
      ...(formProgress?.exerciseNamed
        ? stepCopy.exercise_instructions
        : stepCopy.exercise_name),
    }
  }

  if (!mediaAdded && !state?.media_skipped) {
    return { id: "media", ...stepCopy.media }
  }

  if (!endDateSet) {
    return { id: "end_date", ...stepCopy.end_date }
  }

  if (!scheduleSet) {
    return { id: "schedule", ...stepCopy.schedule }
  }

  return { id: "publish", ...stepCopy.publish }
}

const getFallbackDestination = (stepId, data) => {
  switch (stepId) {
    case "practice_client":
      return data?.practice_client_id
        ? `/client/${data.practice_client_id}`
        : "/clients"
    case "add_program":
      return data?.practice_client_id
        ? `/client/${data.practice_client_id}`
        : "/clients"
    case "exercise":
    case "media":
    case "end_date":
    case "schedule":
    case "publish":
      if (data?.latest_program_id && data?.latest_program_client_id) {
        return `/program/edit/${data.latest_program_client_id}/${data.latest_program_id}`
      }
      return data?.practice_client_id
        ? `/program/add/${data.practice_client_id}`
        : "/clients"
    case "preview":
      return data?.latest_program_id
        ? `/program/${data.latest_program_id}`
        : "/clients"
    default:
      return "/clients"
  }
}

const getReviewDestination = (step, data) => {
  if (getWalkthroughTipKey(step) === getWalkthroughTipKey(cueSequence[0])) {
    return "/clients"
  }

  return getFallbackDestination(step.id, data)
}

const getFallbackLabel = (stepId) => {
  switch (stepId) {
    case "practice_client":
      return "Open Test Client"
    case "add_program":
      return "Open Test Client"
    case "exercise":
    case "media":
    case "end_date":
    case "schedule":
    case "publish":
      return "Continue test program"
    case "preview":
      return "Open test program"
    default:
      return "Continue"
  }
}

const TherapistWalkthrough = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const isTherapist = authService.isTherapist()

  const [data, setData] = useState(null)
  const [loadError, setLoadError] = useState(false)
  const [formProgress, setFormProgress] = useState(null)
  const [cueHost, setCueHost] = useState(null)
  const [interactionOpen, setInteractionOpen] = useState(false)
  const [targetFound, setTargetFound] = useState(false)
  const [engagedCueKeys, setEngagedCueKeys] = useState(() => new Set())
  const historySyncRef = useRef({
    locationKey: null,
    path: null,
    liveTipKey: null,
  })
  const historicalReviewRef = useRef({
    locationKey: null,
    cueKey: null,
  })

  const walkthroughIsTerminal = Boolean(
    data && (!data.eligible || data.waiting_for_terms || data.completed)
  )
  const walkthroughIsActive = isTherapist && Boolean(data?.eligible) && !walkthroughIsTerminal

  const loadState = useCallback(async () => {
    if (!isTherapist) return

    setLoadError(false)
    try {
      const response = await api.get("/therapist-walkthrough/")
      setData(response.data)
    } catch (error) {
      setLoadError(true)
      console.error("Could not load therapist walkthrough:", error)
    }
  }, [isTherapist])

  useEffect(() => {
    if (walkthroughIsTerminal) return
    loadState()
  }, [
    loadState,
    location.pathname,
    location.search,
    walkthroughIsTerminal,
  ])

  useEffect(() => {
    const handleFormProgress = (event) => {
      setFormProgress(event.detail || null)
    }

    const handleRefresh = () => {
      loadState()
    }

    window.addEventListener(
      WALKTHROUGH_FORM_PROGRESS_EVENT,
      handleFormProgress
    )
    window.addEventListener(WALKTHROUGH_REFRESH_EVENT, handleRefresh)

    return () => {
      window.removeEventListener(
        WALKTHROUGH_FORM_PROGRESS_EVENT,
        handleFormProgress
      )
      window.removeEventListener(WALKTHROUGH_REFRESH_EVENT, handleRefresh)
    }
  }, [loadState])

  useEffect(() => {
    const handlePreviewViewed = (event) => {
      if (walkthroughIsTerminal || location.pathname.replace(/\/$/, "") !== `/program/${event.detail?.programId}`) return
      loadState()
      navigate(`${location.pathname}${location.search}`, {
        state: {
          ...(location.state || {}),
          [WALKTHROUGH_HISTORY_STATE_KEY]: createWalkthroughHistoryCue({ id: "complete", ...stepCopy.complete }),
        },
      })
    }
    window.addEventListener(WALKTHROUGH_PREVIEW_VIEWED_EVENT, handlePreviewViewed)
    return () => window.removeEventListener(WALKTHROUGH_PREVIEW_VIEWED_EVENT, handlePreviewViewed)
  }, [loadState, navigate, location.pathname, location.search, location.state, walkthroughIsTerminal])

  useEffect(() => {
    if (!/^\/program\/(add|edit)\//.test(location.pathname)) {
      setFormProgress(null)
    }
  }, [location.pathname])

  useEffect(() => {
    const update = () => setInteractionOpen(Boolean(document.querySelector(
      '.media-picker-camera-backdrop, [data-walkthrough-client-preview="true"]'
    )))
    const observer = new MutationObserver(update)
    observer.observe(document.body, { childList: true, subtree: true })
    update()
    return () => observer.disconnect()
  }, [])

  const liveCurrentStep = useMemo(() => {
    if (
      !isTherapist ||
      !data?.eligible ||
      data?.waiting_for_terms ||
      data?.completed
    ) {
      return null
    }

    const state = data.state || {}
    const path = location.pathname
    const practiceClientPath = data.practice_client_id
      ? `/client/${data.practice_client_id}`
      : null
    const isProgramForm = /^\/program\/(add|edit)\//.test(path)
    const isProgramDetails = /^\/program\/\d+\/?$/.test(path)

    if (!state.practice_client_ready) {
      return { id: "practice_client", ...stepCopy.practice_client }
    }

    if (!state.program_created && !isProgramForm) {
      return path === practiceClientPath
        ? { id: "add_program", ...stepCopy.add_program }
        : {
            id: "practice_client",
            ...stepCopy.practice_client,
            fallbackOnly: path !== "/clients",
          }
    }

    if (isProgramForm && !state.program_published) {
      return getProgramFormStep(state, formProgress)
    }

    if (!state.program_published) {
      const step = getProgramFormStep(state, null)
      return { ...step, fallbackOnly: true }
    }

    if (!state.previewed) {
      return isProgramDetails
        ? { id: "preview", ...stepCopy.preview }
        : { id: "preview", ...stepCopy.preview, fallbackOnly: true }
    }

    return {
      id: "complete",
      ...stepCopy.complete,
      fallbackOnly: !isProgramDetails,
    }
  }, [data, formProgress, isTherapist, location.pathname])

  const liveTipKey = getWalkthroughTipKey(liveCurrentStep)
  const storedHistoryCue =
    location.state?.[WALKTHROUGH_HISTORY_STATE_KEY] || null
  const isReviewingHistoricalCue = Boolean(
    liveCurrentStep &&
      storedHistoryCue?.key &&
      storedHistoryCue.key !== liveTipKey &&
      storedHistoryCue.step
  )
  const currentStep = isReviewingHistoricalCue
    ? {
        ...storedHistoryCue.step,
        reviewOnly: true,
      }
    : liveCurrentStep
  const currentTipKey = getWalkthroughTipKey(currentStep)
  const currentStepSelector = currentStep?.selector || null
  const currentStepFallbackOnly = Boolean(currentStep?.fallbackOnly)

  useLayoutEffect(() => {
    if (!walkthroughIsActive) return undefined
    document.body.classList.add("therapist-walkthrough-active")
    const unlock = lockWalkthroughInteractions(() => {
      const camera = document.querySelector(".media-picker-camera-backdrop")
      if (camera) return [camera]
      const preview = document.querySelector('[data-walkthrough-client-preview="true"]')
      if (preview) return [preview, ...document.querySelectorAll(".therapist-walkthrough-card")]
      return [
        currentStepSelector && !currentStepFallbackOnly
          ? document.querySelector(currentStepSelector)
          : null,
        ...document.querySelectorAll(".therapist-walkthrough-card"),
      ]
    })
    return () => {
      unlock()
      document.body.classList.remove("therapist-walkthrough-active")
    }
  }, [walkthroughIsActive, currentStepSelector, currentStepFallbackOnly])

  useLayoutEffect(() => {
    if (!currentTipKey || !currentStepSelector || interactionOpen) return undefined

    const recordEngagement = (event) => {
      const target = document.querySelector(currentStepSelector)
      if (!target?.contains(event.target)) return
      setEngagedCueKeys((keys) => {
        if (keys.has(currentTipKey)) return keys
        return new Set([...keys, currentTipKey])
      })
    }
    const events = ["focus", "focusin", "click", "input", "change", "keydown"]
    events.forEach((event) => document.addEventListener(event, recordEngagement, true))
    return () => events.forEach((event) => document.removeEventListener(event, recordEngagement, true))
  }, [currentTipKey, currentStepSelector, interactionOpen])

  useEffect(() => {
    const currentPath = `${location.pathname}${location.search}`
    const previous = historySyncRef.current
    const locationChanged =
      Boolean(previous.locationKey) && previous.locationKey !== location.key
    const pathChanged = Boolean(previous.path) && previous.path !== currentPath
    const liveChanged =
      Boolean(previous.liveTipKey) && previous.liveTipKey !== liveTipKey

    if (!liveCurrentStep || !liveTipKey) {
      historySyncRef.current = {
        locationKey: location.key,
        path: currentPath,
        liveTipKey,
      }
      return
    }

    const nextHistoryCue = createWalkthroughHistoryCue(liveCurrentStep)
    const nextState = {
      ...(location.state || {}),
      [WALKTHROUGH_HISTORY_STATE_KEY]: nextHistoryCue,
    }
    const activeHistoricalReview = historicalReviewRef.current
    const resumedHistoricalCue = Boolean(
      activeHistoricalReview.locationKey === location.key &&
        activeHistoricalReview.cueKey === storedHistoryCue?.key &&
        previous.liveTipKey === storedHistoryCue?.key &&
        liveTipKey !== storedHistoryCue?.key
    )

    if (isReviewingHistoricalCue) {
      historicalReviewRef.current = {
        locationKey: location.key,
        cueKey: storedHistoryCue.key,
      }
    }

    if (!storedHistoryCue?.key) {
      historySyncRef.current = {
        locationKey: location.key,
        path: currentPath,
        liveTipKey,
      }
      historicalReviewRef.current = {
        locationKey: null,
        cueKey: null,
      }
      navigate(currentPath, {
        replace: true,
        state: nextState,
      })
      return
    }

    if (resumedHistoricalCue) {
      historySyncRef.current = {
        locationKey: location.key,
        path: currentPath,
        liveTipKey,
      }
      historicalReviewRef.current = {
        locationKey: null,
        cueKey: null,
      }
      navigate(currentPath, {
        replace: true,
        state: nextState,
      })
      return
    }

    const liveProgressedOnCurrentEntry =
      !locationChanged &&
      !pathChanged &&
      liveChanged &&
      storedHistoryCue.key === previous.liveTipKey

    if (liveProgressedOnCurrentEntry) {
      historySyncRef.current = {
        locationKey: location.key,
        path: currentPath,
        liveTipKey,
      }
      historicalReviewRef.current = {
        locationKey: null,
        cueKey: null,
      }
      navigate(currentPath, {
        state: nextState,
      })
      return
    }

    if (locationChanged && !isReviewingHistoricalCue) {
      historicalReviewRef.current = {
        locationKey: null,
        cueKey: null,
      }
    }

    historySyncRef.current = {
      locationKey: location.key,
      path: currentPath,
      liveTipKey,
    }
  }, [
    isReviewingHistoricalCue,
    liveCurrentStep,
    liveTipKey,
    location.key,
    location.pathname,
    location.search,
    location.state,
    navigate,
    storedHistoryCue,
  ])

  useEffect(() => {
    if (!isReviewingHistoricalCue || !currentStep?.id) return

    window.dispatchEvent(
      new CustomEvent(WALKTHROUGH_REVIEW_CUE_EVENT, {
        detail: { stepId: currentStep.id },
      })
    )
  }, [currentStep?.id, isReviewingHistoricalCue, location.key])

  useEffect(() => {
    if (!liveCurrentStep || data?.started) return

    api.post("/therapist-walkthrough/", { action: "start" })
      .then((response) => setData(response.data))
      .catch((error) => {
        console.error("Could not start therapist walkthrough:", error)
      })
  }, [liveCurrentStep, data?.started])

  useEffect(() => {
    if (!currentTipKey || currentStepFallbackOnly || (interactionOpen && currentStepSelector !== stepCopy.complete.selector)) {
      setCueHost(null)
      setTargetFound(false)
      return undefined
    }

    let activeTarget = null
    let animationFrame = null
    let scrollTimeout = null
    const host = document.createElement("div")
    host.className = "therapist-walkthrough-cue-host"

    const keepFocusedCueVisible = () => {
      if (activeTarget?.contains(document.activeElement)) {
        host.scrollIntoView({ block: "start", inline: "nearest" })
      }
    }

    const updateTarget = () => {
      const target = document.querySelector(currentStepSelector)

      if (!target) {
        activeTarget?.classList.remove("therapist-walkthrough-target")
        activeTarget = null
        setTargetFound(false)
        setCueHost(null)
        return
      }

      if (activeTarget && activeTarget !== target) {
        activeTarget.classList.remove("therapist-walkthrough-target")
      }

      activeTarget = target
      if (currentStepSelector !== stepCopy.complete.selector && !activeTarget.classList.contains("therapist-walkthrough-target")) {
        activeTarget.classList.add("therapist-walkthrough-target")
      }
      setTargetFound(true)

      // Put the cue before a whole horizontal button/header row, so
      // inserting guidance cannot squeeze its buttons on narrow screens.
      let anchor = target
      while (anchor.parentElement && anchor.parentElement !== document.body) {
        const style = window.getComputedStyle(anchor.parentElement)
        if (style.display !== "flex" || style.flexDirection.startsWith("column")) break
        anchor = anchor.parentElement
      }
      if (host.nextSibling !== anchor) {
        anchor.parentNode.insertBefore(host, anchor)
        setCueHost(host)
      }
    }

    updateTarget()
    if (currentStepSelector !== stepCopy.complete.selector) scrollTimeout = window.setTimeout(() => {
      const target = document.querySelector(currentStepSelector)
      updateTarget()
      const scrollTarget = host.isConnected ? host : target
      scrollTarget?.scrollIntoView({
        behavior: "smooth",
        block: "start",
        inline: "nearest",
      })

      animationFrame = window.requestAnimationFrame(updateTarget)
    }, 120)

    const observer = new MutationObserver(updateTarget)
    // Watch for route/content mounts only. Observing attributes here creates
    // a feedback loop because this walkthrough updates classes and inline
    // styles while positioning its own highlight.
    observer.observe(document.body, {
      childList: true,
      subtree: true,
    })

    window.visualViewport?.addEventListener("resize", keepFocusedCueVisible)
    window.addEventListener("resize", updateTarget)
    window.addEventListener("scroll", updateTarget, true)

    return () => {
      window.clearTimeout(scrollTimeout)
      if (animationFrame) {
        window.cancelAnimationFrame(animationFrame)
      }
      observer.disconnect()
      host.remove()
      window.visualViewport?.removeEventListener("resize", keepFocusedCueVisible)
      window.removeEventListener("resize", updateTarget)
      window.removeEventListener("scroll", updateTarget, true)
      activeTarget?.classList.remove("therapist-walkthrough-target")
    }
  }, [
    currentTipKey,
    currentStepFallbackOnly,
    currentStepSelector,
    interactionOpen,
  ])

  const handleReviewCue = (cue) => {
    if (!cue) return

    navigate(getReviewDestination(cue, data), {
      state: {
        ...(location.state || {}),
        [WALKTHROUGH_HISTORY_STATE_KEY]: createWalkthroughHistoryCue(cue),
      },
    })
  }

  const handleComplete = async () => {
    try {
      const response = await api.post("/therapist-walkthrough/", {
        action: "complete",
      })
      setData(response.data)
      navigate("/clients")
    } catch (error) {
      console.error("Could not complete therapist walkthrough:", error)
    }
  }

  // Stay hidden until eligibility is known; existing therapists also mount
  // this component while their walkthrough status is being checked.
  if (isTherapist && !data && loadError) {
    return (
      <section className="therapist-walkthrough-card therapist-walkthrough-card--bottom"
        role="alert">
          <p>We couldn't load your tutorial. Please try again.</p>
          <Button variant="primary" onClick={loadState}>Retry tutorial</Button>
      </section>
    )
  }

  if (!currentStep || (interactionOpen && currentStep.id !== "complete")) return null

  const currentCueIndex = getCueSequenceIndex(currentStep)
  const liveCueIndex = getCueSequenceIndex(liveCurrentStep)
  const previousCue =
    currentCueIndex > 0 ? cueSequence[currentCueIndex - 1] : null
  const nextCue = cueSequence[currentCueIndex + 1] || null
  const hasEngaged = engagedCueKeys.has(currentTipKey) || currentCueIndex < liveCueIndex
  // Navigation must never replace sending a program or opening its preview.
  const actionComplete = currentStep.id === "publish"
    ? Boolean(data?.state?.program_published)
    : currentStep.id === "preview"
      ? Boolean(data?.state?.previewed)
      : true
  const canGoNext = Boolean(nextCue && hasEngaged && actionComplete)
  const handleNextCue = () => {
    if (!canGoNext) return
    // Leaving an input commits its normal blur-driven walkthrough progress.
    document.activeElement?.blur()
    handleReviewCue(nextCue)
  }
  const navigation = (
    <div className="therapist-walkthrough-actions">
      <Button variant="outline-secondary" size="sm" disabled={!previousCue}
        onClick={() => handleReviewCue(previousCue)}>
        Previous cue
      </Button>
      {currentStep.id !== "complete" && (
        <Button variant={canGoNext ? "primary" : "secondary"} size="sm"
          className="therapist-walkthrough-next" disabled={!canGoNext} onClick={handleNextCue}>
          Next cue
        </Button>
      )}
    </div>
  )

  const fallbackDestination = getFallbackDestination(currentStep.id, data)
  const card = (
      <section
        className={`therapist-walkthrough-card ${cueHost ? "therapist-walkthrough-card--inline" : "therapist-walkthrough-card--bottom"}`}
        role="dialog"
        aria-live="polite"
        aria-label={currentStep.title}
      >
        <div className="therapist-walkthrough-step">
          Step {currentStep.number} of {STEP_COUNT}
        </div>
        <h3>{currentStep.title}</h3>
        <p>{currentStep.body}</p>

        {navigation}
        <div className="therapist-walkthrough-actions">
          {currentStep.id === "complete" && !currentStep.reviewOnly ? (
            <Button variant="primary" onClick={handleComplete}>
              Finish onboarding
            </Button>
          ) : (
            !currentStep.reviewOnly &&
            (!targetFound || currentStep.fallbackOnly) && (
              <Button
                variant="primary"
                onClick={() => navigate(fallbackDestination)}
              >
                {getFallbackLabel(currentStep.id)}
              </Button>
            )
          )}
        </div>

      </section>
  )

  return cueHost ? createPortal(card, cueHost) : card
}

export default TherapistWalkthrough


